import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, Subject } from 'rxjs';
import { tap, catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';
import { SessionService } from '../modules/auth/services/session.service';
import { PlatformService } from '@app/shared/services/platform.service';
import { LoggerService } from '@app/shared/services/logger.service';

/**
 * Token Refresh Interceptor
 *
 * Odpowiada za:
 * 1. Session rotation - odświeżanie tokenu gdy wygasa wkrótce
 * 2. Obsługa 401 błędów - automatyczne odświeżanie tokenu
 * 3. Queue requests - gdy refresh jest w toku, kolejkuje requesty
 * 4. Token expired - czeka na refresh ZANIM wyśle request z wygasłym tokenem
 *
 * WAŻNE: Używa SessionService (nie AuthService!) aby uniknąć circular dependency.
 * AuthInterceptor dodaje Authorization header.
 */
@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  /** Emituje true przy sukcesie, false przy błędzie refresh */
  private refreshResult$ = new Subject<boolean>();

  private router = inject(Router);
  private messageService = inject(MessageService);
  private translocoService = inject(TranslocoService);
  private platformService = inject(PlatformService);
  private logger = inject(LoggerService);

  constructor(
    private sessionService: SessionService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Pomijaj refresh dla endpointów publicznych
    if (this.isPublicAuthEndpoint(request.url)) {
      return next.handle(request);
    }

    // Blokuj refresh w trakcie logout - nie próbuj odświeżać sesji
    if (this.sessionService.isLoggingOut) {
      return next.handle(request);
    }

    // Jeśli token jest już wygasły - poczekaj na refresh ZANIM wyślesz request
    if (this.isTokenExpired()) {
      return this.waitForRefreshAndRetry(request, next);
    }

    // Sprawdź czy token wygasa wkrótce - proactive refresh (fire-and-forget)
    this.checkAndRotateTokenIfNeeded();

    return next.handle(request).pipe(
      tap((event) => {
        // Sprawdź czy backend zwrócił nowy token w response
        if (event instanceof HttpResponse) {
          this.extractAndUpdateTokenFromResponse(event);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // 401 - Unauthorized - spróbuj refresh token
        if (error.status === 401 && !this.isTokenRefreshEndpoint(request.url)) {
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Sprawdź czy token jest już wygasły (nie "wkrótce wygaśnie", lecz TERAZ wygasły)
   */
  private isTokenExpired(): boolean {
    return !this.sessionService.isTokenValid() && !!this.sessionService.getAccessToken();
  }

  /**
   * Token jest wygasły - uruchom refresh (jeśli nie jest w toku) i poczekaj na nowy token
   * ZANIM wyślesz request. Zapobiega wysyłaniu requestów z wygasłym tokenem.
   */
  private waitForRefreshAndRetry(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Uruchom refresh jeśli jeszcze nie jest w toku
    this.startRefreshIfNeeded();

    // Czekaj na wynik refresha
    return this.refreshTokenSubject.pipe(
      filter(token => token != null),
      take(1),
      switchMap(token => {
        if (token === '__REFRESH_FAILED__') {
          this.logger.warn('[TokenRefresh] Refresh failed, dropping request');
          return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Token refresh failed' }));
        }
        this.logger.log('[TokenRefresh] Token refreshed, sending request with new token');
        const cloned = request.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        });
        return next.handle(cloned);
      })
    );
  }

  /**
   * Uruchom refresh jeśli nie jest w toku
   */
  private startRefreshIfNeeded(): void {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const isMobile = this.platformService.isMobile();
    this.logger.log('[TokenRefresh] Token expired, starting refresh before sending requests', {
      platform: isMobile ? 'mobile' : 'web'
    });

    if (isMobile && !this.sessionService.getRefreshToken()) {
      this.logger.log('[TokenRefresh] Mobile: No refresh token available');
      this.isRefreshing = false;
      // Sentinel odblokowuje requesty zaparkowane w waitForRefreshAndRetry —
      // bez tego czekałyby na subjekcie w nieskończoność.
      this.refreshTokenSubject.next('__REFRESH_FAILED__');
      this.handleLogout(false);
      return;
    }

    this.sessionService.refreshToken().subscribe({
      next: () => {
        this.logger.log('[TokenRefresh] Refresh successful');
        this.isRefreshing = false;
        const newToken = this.sessionService.getAccessToken();
        this.refreshTokenSubject.next(newToken);
      },
      error: (error) => {
        this.logger.error('[TokenRefresh] Refresh failed:', error);
        this.isRefreshing = false;

        const status = error?.status;
        const isAuthError = status === 401 || status === 403;

        if (isAuthError) {
          this.handleLogout();
        } else {
          // Serwer/sieć - nie wylogowuj, ale odblokuj czekające requesty błędem
          this.logger.warn('[TokenRefresh] Server/network error during refresh');
        }

        // Emituj specjalny token żeby odblokować czekające requesty
        // (puszczamy je dalej - dostaną 401 i zostaną obsłużone normalnie)
        this.refreshTokenSubject.next('__REFRESH_FAILED__');
      },
    });
  }

  /**
   * Proactive refresh - sprawdź czy token wygasa wkrótce i odśwież z wyprzedzeniem.
   * Tylko dla tokenów które SĄ jeszcze ważne ale wkrótce wygasną.
   */
  private checkAndRotateTokenIfNeeded(): void {
    if (this.isRefreshing) {
      return;
    }

    // Tylko jeśli token jest ważny ALE wkrótce wygaśnie
    if (!this.sessionService.isTokenValid()) {
      return;
    }

    const isMobile = this.platformService.isMobile();
    const hasRefreshToken = isMobile ? !!this.sessionService.getRefreshToken() : true;

    if (this.sessionService.isTokenExpiringSoon() && hasRefreshToken) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      this.logger.log('[TokenRefresh] Proactive token refresh - token expiring soon', {
        platform: isMobile ? 'mobile' : 'web'
      });

      this.sessionService.refreshToken().subscribe({
        next: () => {
          this.logger.log('[TokenRefresh] Proactive refresh successful');
          this.isRefreshing = false;
          const newToken = this.sessionService.getAccessToken();
          this.refreshTokenSubject.next(newToken);
        },
        error: (error) => {
          this.logger.error('[TokenRefresh] Proactive refresh failed:', error);
          this.isRefreshing = false;
          // Proaktywny refresh się nie udał - token jeszcze ważny,
          // requesty pójdą normalnie, 401 obsłuży handle401Error
          this.refreshTokenSubject.next(this.sessionService.getAccessToken());
        },
      });
    }
  }

  /**
   * Dodaj Authorization header do requestu
   */
  private addAuthorizationHeader(
    request: HttpRequest<unknown>,
    authHeader: string
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: authHeader,
      },
    });
  }

  /**
   * Sprawdź czy response zawiera nowy token i zaktualizuj go
   * (Backend może zwrócić nowy token w dowolnym response)
   */
  private extractAndUpdateTokenFromResponse(response: HttpResponse<unknown>): void {
    // Sprawdź Authorization header
    const authHeader = response.headers.get('Authorization') || response.headers.get('authorization');
    if (authHeader && authHeader.toLowerCase().startsWith('bearer ')) {
      const newToken = authHeader.slice('Bearer '.length).trim();
      if (newToken && newToken.length > 0) {
        this.logger.log('[TokenRefresh] New token received from Authorization header');
        // Token rotation handled by SessionService
      }
    }
  }

  /**
   * Obsługa 401 - Unauthorized
   * Kolejkuje requesty gdy refresh jest w toku
   */
  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Nie próbuj refresha w trakcie logout
    if (this.sessionService.isLoggingOut) {
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Logging out' }));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // ⚠️ DUAL MODE:
      // Web: refresh token jest w HttpOnly Cookie (nie mamy dostępu, ale przeglądarka wyśle)
      // Mobile: refresh token jest w SessionService
      const isMobile = this.platformService.isMobile();
      const refreshToken = this.sessionService.getRefreshToken();

      // Web: zawsze próbuj refresh (cookie się wyśle automatycznie)
      // Mobile: sprawdź czy mamy refresh token
      if (isMobile && !refreshToken) {
        this.logger.log('[TokenRefresh] Mobile: No refresh token available');
        this.isRefreshing = false;
        // Sentinel odblokowuje requesty zakolejkowane w gałęzi "refresh w toku"
        this.refreshTokenSubject.next('__REFRESH_FAILED__');
        this.handleLogout(false); // Brak tokena - nie pokazuj "sesja wygasła"
        return throwError(() => new Error('No refresh token available'));
      }

      this.logger.log('[TokenRefresh] 401 received, attempting token refresh...', {
        platform: isMobile ? 'mobile' : 'web',
        hasRefreshToken: isMobile ? !!refreshToken : 'N/A (cookie)'
      });

      return this.sessionService.refreshToken().pipe(
        switchMap(() => {
          this.isRefreshing = false;
          const newToken = this.sessionService.getAccessToken();

          if (newToken) {
            this.logger.log('[TokenRefresh] Refresh successful, retrying original request');
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addAuthorizationHeader(request, `Bearer ${newToken}`));
          } else {
            this.logger.error('[TokenRefresh] No new token after refresh');
            this.handleLogout();
            return throwError(() => new Error('Failed to refresh token'));
          }
        }),
        catchError((error) => {
          this.logger.error('[TokenRefresh] Refresh failed:', error);
          this.isRefreshing = false;

          const status = error?.status;
          const isAuthError = status === 401 || status === 403;
          const isServerError = status >= 500 && status < 600;
          const isNetworkError = status === 0 || !status;

          if (isAuthError) {
            this.logger.log('[TokenRefresh] Auth error - logging out');
            this.handleLogout();
          } else if (isServerError || isNetworkError) {
            this.logger.warn('[TokenRefresh] Server/network error - keeping session, user can retry');
          } else {
            this.logger.log('[TokenRefresh] Unknown error - logging out');
            this.handleLogout();
          }

          // Odblokuj czekające requesty - emituj sentinel żeby nie wisiały w nieskończoność
          this.refreshTokenSubject.next('__REFRESH_FAILED__');

          return throwError(() => error);
        })
      );
    } else {
      // Refresh już w toku - czekaj na nowy token i ponów request
      this.logger.log('[TokenRefresh] Refresh in progress, queuing request...');
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          if (token === '__REFRESH_FAILED__') {
            return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Token refresh failed' }));
          }
          this.logger.log('[TokenRefresh] Retrying queued request with new token');
          return next.handle(this.addAuthorizationHeader(request, `Bearer ${token}`));
        })
      );
    }
  }

  /**
   * Wyloguj użytkownika i przekieruj do logowania
   * @param showToast - czy pokazać toast o wygaśnięciu sesji (default: true)
   */
  private handleLogout(showToast = true): void {
    this.sessionService.clearSession();
    // Defensywnie zeruj bramkę refresh: cały brak-regresji sentinela opiera się
    // na inwariancie "każdy nowy cykl refresh robi next(null) na starcie". Reset
    // przy logout gwarantuje, że zalegający __REFRESH_FAILED__ nie odrzuci
    // requestów po ponownym zalogowaniu, nawet gdyby ktoś dodał ścieżkę bez resetu.
    this.isRefreshing = false;
    this.refreshTokenSubject.next(null);

    if (showToast) {
      const summary = this.translocoService.translate('errors.errorMessageTitle');
      const detail = this.translocoService.translate('errors.AUTH.SESSION_EXPIRED');
      this.messageService.add({ severity: 'warn', summary, detail, life: 5000 });
    }

    this.router.navigate(['/auth/login']);
  }

  /**
   * Sprawdź czy to endpoint odświeżania tokenu
   */
  private isTokenRefreshEndpoint(url: string): boolean {
    return url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/verify-mfa');
  }

  /**
   * Sprawdź czy to publiczny endpoint autentykacji
   */
  private isPublicAuthEndpoint(url: string): boolean {
    const publicEndpoints = [
      '/auth/activate-account',
      '/auth/activate',
      '/auth/reset-password',
      '/auth/request-password-reset',
      '/auth/register',
      '/auth/guest-login',
      '/auth/resend-activation',
      '/auth/login',
      '/auth/logout',
      '/auth/refresh'  // ✅ NIE dodawaj Authorization header (używamy cookie!)
    ];
    return publicEndpoints.some(endpoint => url.includes(endpoint));
  }
}
